// @spec ANL-STR-013
// @spec ANL-STR-014
// @spec ANL-STR-016
'use client';

import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Check, Loader2, Pencil, Plus, Save } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { TooltipProvider } from '@/components/ui/tooltip';
import { DashboardToolbar } from '@/features/analytics/components';
import { EmptyDashboard, useDashboard } from '@/features/analytics/custom-dashboards';
import type { WidgetType } from '@/features/analytics/custom-dashboards';
import {
  DashboardGrid,
  QuickAddDialog,
  UndoButton,
  WidgetLibrary,
  WidgetConfigDialog,
  useDashboardLayout,
} from '@/features/analytics/dashboard-editor';
import type { WidgetTemplate } from '@/features/analytics/dashboard-editor';
import { AddToReportDialog } from '@/features/analytics/report-studio/components/AddToReportDialog';
import { useActiveOrganizationId } from '@/hooks/use-active-org-id';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/analytics/dashboards/$dashboardId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.dashboard'),
  }),
  component: DashboardDetailPage,
});

/**
 * Single dashboard view page with edit mode.
 *
 * Shows dashboard content with widgets, edit toolbar when editing,
 * and empty state with widget library preview if no widgets have been added yet.
 */
function DashboardDetailPage() {
  const { dashboardId } = Route.useParams();
  const { organizationId } = useActiveOrganizationId();
  const orgId = organizationId ?? '';

  const dashboardContentRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isWidgetLibraryOpen, setIsWidgetLibraryOpen] = useState(false);
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [configuringWidgetId, setConfiguringWidgetId] = useState<string | null>(null);
  const [addToReportWidgetId, setAddToReportWidgetId] = useState<string | null>(null);

  const dashboardQuery = useDashboard(orgId, dashboardId);
  const dashboard = dashboardQuery.data;
  const isLoading = dashboardQuery.isLoading;
  const dashboardError = dashboardQuery.error;

  const layoutState = useDashboardLayout({
    dashboardId,
    orgId,
  });
  const {
    layout,
    widgets,
    isSaving,
    canUndo,
    canRedo,
    updateLayout,
    addWidget,
    updateWidgetConfig,
    removeWidget,
    undo,
    redo,
  } = layoutState;
  const isLayoutLoading = layoutState.isLoading;

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    if (!isEditing) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Use userAgentData if available, fallback to userAgent check
      const isMac =
        'userAgentData' in navigator
          ? (navigator.userAgentData as { platform?: string } | undefined)?.platform === 'macOS'
          : navigator.userAgent.toLowerCase().includes('mac');
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      if (modifier && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing, undo, redo]);

  const handleEnterEditMode = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleExitEditMode = useCallback(() => {
    setIsEditing(false);
    setIsWidgetLibraryOpen(false);
  }, []);

  const handleAddWidget = useCallback(
    async (template: WidgetTemplate) => {
      await addWidget(template);
    },
    [addWidget]
  );

  const handleWidgetClick = useCallback((widgetId: string) => {
    // In view mode, could navigate to detail or open modal
    console.log('Widget clicked:', widgetId);
  }, []);

  const handleWidgetConfigure = useCallback((widgetId: string) => {
    setConfiguringWidgetId(widgetId);
  }, []);

  const handleWidgetRemove = useCallback(
    (widgetId: string) => {
      void removeWidget(widgetId);
    },
    [removeWidget]
  );

  const [quickAddWidgetType, setQuickAddWidgetType] = useState<WidgetType | undefined>();

  const handleQuickAddFromEmpty = useCallback(
    (widgetType: WidgetType) => {
      setIsEditing(true);
      setQuickAddWidgetType(widgetType);
      setIsQuickAddOpen(true);
    },
    []
  );

  const handleWidgetAddToReport = useCallback((widgetId: string) => {
    setAddToReportWidgetId(widgetId);
  }, []);

  const handleCloseConfigDialog = useCallback(() => {
    setConfiguringWidgetId(null);
  }, []);

  const configuringWidget = configuringWidgetId
    ? widgets.find((w) => w.id === configuringWidgetId)
    : undefined;

  if (isLoading || isLayoutLoading) {
    return (
      <div className="min-h-screen bg-background p-6 lg:p-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  if (dashboardError || !dashboard) {
    return (
      <div className="min-h-screen bg-background p-6 lg:p-8">
        <div className="mx-auto max-w-7xl">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <h2 className="text-lg font-semibold">Dashboard not found</h2>
              <p className="mt-2 text-muted-foreground">
                The dashboard you&apos;re looking for doesn&apos;t exist or you don&apos;t have
                access to it.
              </p>
              <Button asChild className="mt-4" variant="outline">
                <Link to="/analytics/dashboards">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Dashboards
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const hasWidgets = widgets.length > 0;

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background p-6 lg:p-8">
        <div className="mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-6 flex items-start justify-between">
            <div>
              <div className="mb-2">
                <Button asChild variant="ghost" size="sm">
                  <Link to="/analytics/dashboards">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Dashboards
                  </Link>
                </Button>
              </div>
              <h1 className="text-2xl font-bold">{dashboard.name}</h1>
              {dashboard.description && (
                <p className="mt-1 text-muted-foreground">{dashboard.description}</p>
              )}
            </div>

            {/* Editor Toolbar */}
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  {/* Undo/Redo buttons */}
                  <UndoButton canUndo={canUndo} canRedo={canRedo} onUndo={undo} onRedo={redo} />

                  <div className="mx-2 h-6 w-px bg-border" />

                  {/* Add Widget button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsQuickAddOpen(true);
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Widget
                  </Button>

                  {/* Save indicator */}
                  {isSaving && (
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Save className="h-4 w-4 animate-pulse" />
                      <span>Saving...</span>
                    </div>
                  )}

                  {/* Done button */}
                  <Button size="sm" onClick={handleExitEditMode}>
                    <Check className="mr-2 h-4 w-4" />
                    Done
                  </Button>
                </>
              ) : (
                <>
                  <DashboardToolbar
                    dashboardRef={dashboardContentRef}
                    dashboardName={dashboard.name}
                  />
                  <Button variant="outline" size="sm" onClick={handleEnterEditMode}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit Dashboard
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Dashboard Content */}
          <div ref={dashboardContentRef}>
            {hasWidgets || isEditing ? (
              <DashboardGrid
                orgId={orgId}
                dashboardId={dashboardId}
                widgets={widgets}
                layout={layout}
                isEditing={isEditing}
                onLayoutChange={updateLayout}
                onWidgetClick={handleWidgetClick}
                onWidgetConfigure={handleWidgetConfigure}
                onWidgetRemove={handleWidgetRemove}
                onWidgetAddToReport={handleWidgetAddToReport}
              />
            ) : (
              <EmptyDashboard
                onAddWidgets={handleEnterEditMode}
                onSelectWidgetType={handleQuickAddFromEmpty}
              />
            )}
          </div>
        </div>

        {/* Quick Add Dialog */}
        <QuickAddDialog
          isOpen={isQuickAddOpen}
          onClose={() => { setIsQuickAddOpen(false); setQuickAddWidgetType(undefined); }}
          onAddWidget={(template) => {
            void handleAddWidget(template);
          }}
          onBrowseAll={() => {
            setIsQuickAddOpen(false);
            setIsWidgetLibraryOpen(true);
          }}
          defaultWidgetType={quickAddWidgetType}
        />

        {/* Widget Library Dialog */}
        <WidgetLibrary
          isOpen={isWidgetLibraryOpen}
          onClose={() => {
            setIsWidgetLibraryOpen(false);
          }}
          onAddWidget={(template) => {
            void handleAddWidget(template);
          }}
          onPreviewWidget={() => {
            // Preview handled internally
          }}
        />

        {/* Widget Configuration Dialog */}
        <WidgetConfigDialog
          widget={configuringWidget ?? null}
          isOpen={!!configuringWidgetId}
          onClose={handleCloseConfigDialog}
          onSave={(updates) => {
            if (configuringWidget) {
              void updateWidgetConfig(configuringWidget.id, updates);
            }
            handleCloseConfigDialog();
          }}
        />

        {/* Add to Report Dialog */}
        <AddToReportDialog
          open={!!addToReportWidgetId}
          onOpenChange={(open) => {
            if (!open) setAddToReportWidgetId(null);
          }}
          widgetId={addToReportWidgetId}
          widgetTitle={
            addToReportWidgetId
              ? widgets.find((w) => w.id === addToReportWidgetId)?.title
              : undefined
          }
        />
      </div>
    </TooltipProvider>
  );
}
